import type { FormEvent, FormHTMLAttributes, ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { CircleNotchIcon } from '@phosphor-icons/react'

/**
 * The two dialog widths. Everything that is a form or a confirmation is `md`;
 * `lg` is for the handful of dialogs that lay out two columns or a table.
 * Anything in between drifts, which is what the launch audit found (five
 * widths for five dialogs that do the same job).
 */
export type DialogPatternSize = 'md' | 'lg'

const sizeClasses: Record<DialogPatternSize, string> = {
  md: 'sm:max-w-[480px]',
  lg: 'sm:max-w-[880px]',
}

export interface DialogPatternProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  icon?: ReactNode
  children: ReactNode
  footer?: ReactNode
  isLoading?: boolean
  onCancel?: () => void
  onSubmit?: ((e?: FormEvent) => void) | (() => void)
  submitLabel?: string
  cancelLabel?: string
  size?: DialogPatternSize
  className?: string
  hideCloseButton?: boolean
  contentClassName?: string
  form?: string
  isSubmitting?: boolean
  isForm?: boolean
  formProps?: FormHTMLAttributes<HTMLFormElement>
  wrapContentInCard?: boolean
  contentCardClassName?: string
  iconSize?: 'sm' | 'md' | 'lg'
  modal?: boolean
}

/**
 * The one dialog shell. Title, width, footer height and dismiss behaviour all
 * live here so individual dialogs cannot drift apart again.
 *
 * Escape always closes: nothing in here intercepts the key, so Radix's own
 * dismiss layer handles it, and a nested popover or combobox takes the first
 * Escape only because it is the higher layer. Focus returns to whatever opened
 * the dialog (see DialogContent). A dialog that must protect unsaved work
 * should pass `onCancel` and put a DiscardChangesDialog behind it rather than
 * swallowing the key.
 */
export function DialogPattern({
  open,
  onOpenChange,
  title,
  description,
  icon,
  children,
  footer,
  isLoading = false,
  onCancel,
  onSubmit,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  size = 'md',
  className,
  hideCloseButton = false,
  contentClassName,
  form,
  isSubmitting = false,
  isForm = false,
  formProps,
  wrapContentInCard = false,
  contentCardClassName,
  iconSize = 'md',
  modal = true,
}: DialogPatternProps) {
  const shouldRenderFooter = footer !== null
  const hasCustomFooter = footer !== undefined && footer !== null

  // Default cancel handler if none provided
  const handleCancel = () => {
    if (onCancel) {
      onCancel()
    } else {
      onOpenChange(false)
    }
  }

  // Icon size classes
  const iconSizeClasses = {
    sm: 'h-8 w-8',
    md: 'h-9 w-9',
    lg: 'h-10 w-10',
  }

  // Content wrapper
  const ContentWrapper = isForm ? 'form' : 'div'
  const contentWrapperProps = isForm
    ? {
        onSubmit: onSubmit,
        ...formProps,
      }
    : {}

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={modal}>
      <DialogContent
        className={cn(
          sizeClasses[size],
          'flex flex-col gap-0 overflow-hidden rounded-lg border bg-background p-0 shadow-lg',
          className,
        )}
        hideCloseButton={hideCloseButton}
      >
        <ContentWrapper
          {...contentWrapperProps}
          className="flex h-full min-h-0 flex-col"
        >
          {/* Header section - fixed */}
          <div className="flex flex-shrink-0 items-start gap-3 px-5 pb-3 pt-5">
            {icon && (
              <div
                className={cn(
                  'flex flex-shrink-0 items-center justify-center rounded-lg border border-primary/20 bg-primary/10',
                  iconSizeClasses[iconSize],
                )}
              >
                {icon}
              </div>
            )}

            <DialogHeader className="min-w-0 flex-1 space-y-1 pr-8 text-left">
              <DialogTitle className="text-base font-semibold leading-snug tracking-tight">
                {title}
              </DialogTitle>
              {description && (
                <DialogDescription className="text-sm">
                  {description}
                </DialogDescription>
              )}
            </DialogHeader>
          </div>

          {/* Content section - scrollable */}
          <div
            className={cn('flex-1 overflow-y-auto px-5 py-3', contentClassName)}
          >
            {wrapContentInCard ? (
              <div
                className={cn(
                  'rounded-lg border bg-card p-4 sm:p-5',
                  contentCardClassName,
                )}
              >
                {children}
              </div>
            ) : (
              children
            )}
          </div>

          {/* Footer section - fixed */}
          {shouldRenderFooter && (
            <DialogFooter className="mt-0 flex-shrink-0 border-t border-border/40 bg-muted/20 px-5 pb-3 pt-3 sm:justify-end">
              {hasCustomFooter ? (
                footer
              ) : (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleCancel}
                    disabled={isLoading}
                    className="text-sm"
                  >
                    {cancelLabel}
                  </Button>
                  {onSubmit && (
                    <Button
                      type={form ? 'submit' : 'button'}
                      size="sm"
                      onClick={form ? undefined : onSubmit}
                      disabled={isLoading || isSubmitting}
                      className="min-w-[100px] text-sm"
                      form={form}
                    >
                      {isLoading ? (
                        <>
                          <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                          Loading...
                        </>
                      ) : isSubmitting ? (
                        <>
                          <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        submitLabel
                      )}
                    </Button>
                  )}
                </>
              )}
            </DialogFooter>
          )}
        </ContentWrapper>
      </DialogContent>
    </Dialog>
  )
}
